/**
 * Google Analytics 4 (gtag.js), prod only.
 *
 * `runtimeConfig.public.gaMeasurementId` is empty unless `build_front prod`
 * sets NUXT_PUBLIC_GA_MEASUREMENT_ID, so dev/qa/uat and localhost send nothing.
 *
 * Page views are sent manually on `page:finish` rather than by GA's
 * "page changes based on browser history events" enhanced measurement:
 * history fires on pushState, before the new page sets its title. Turn that
 * toggle OFF in the GA web stream or every navigation counts twice.
 *
 * Consent mode v2 (Thai PDPA): analytics storage stays denied until the
 * visitor accepts in <AnalyticsConsentBanner>. While denied, gtag sets no
 * cookies and sends cookieless pings GA uses for modelled counts. The site
 * runs no ads, so ad signals are always denied.
 */

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  const { gaMeasurementId } = useRuntimeConfig().public as { gaMeasurementId?: string }
  const host = window.location.hostname

  if (!gaMeasurementId || host === 'localhost' || host === '127.0.0.1') {
    return
  }

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaMeasurementId)}`
  document.head.appendChild(script)

  window.dataLayer = window.dataLayer || []
  // gtag.js reads the `arguments` object, not an array — keep the classic form.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments)
  }
  const consent = useAnalyticsConsent()
  const analyticsStorage = () => consent.value === 'granted' ? 'granted' : 'denied'

  // Must be queued before `config`, or the first hit goes out without it.
  window.gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: analyticsStorage()
  })
  window.gtag('js', new Date())
  window.gtag('config', gaMeasurementId, { send_page_view: false })

  watch(consent, () => {
    window.gtag?.('consent', 'update', { analytics_storage: analyticsStorage() })
    if (consent.value !== 'granted') clearGaCookies()
  })

  const router = useRouter()
  let lastPath = ''

  nuxtApp.hook('page:finish', () => {
    const path = router.currentRoute.value.fullPath
    if (path === lastPath) return
    lastPath = path
    // unhead flushes title changes on a setTimeout(0); wait past it.
    setTimeout(() => {
      window.gtag?.('event', 'page_view', {
        page_location: window.location.href,
        page_path: path,
        page_title: document.title
      })
    }, 50)
  })
})

// Withdrawing consent must also remove cookies GA already set. GA writes them
// on the broadest domain it can (e.g. `.zeer.co.th`), so expire the name on
// every parent domain; browsers ignore the public-suffix attempts.
function clearGaCookies() {
  const names = document.cookie
    .split(';')
    .map(c => c.split('=')[0]!.trim())
    .filter(name => name === '_ga' || name.startsWith('_ga_'))
  const labels = window.location.hostname.split('.')

  for (const name of names) {
    document.cookie = `${name}=; Max-Age=0; path=/`
    for (let i = 0; i < labels.length - 1; i++) {
      document.cookie = `${name}=; Max-Age=0; path=/; domain=.${labels.slice(i).join('.')}`
    }
  }
}
